// R13 — THE SEARCH HIT: the one law of how a matched passage is marked and unmarked.
//
// WHY THIS FILE EXISTS. The server wraps what it matched in sentinel CHARACTERS rather than HTML
// (`<mark>` in a database string reaching a React tree is either escaped and ugly or unescaped and an
// injection), and the client splits on those characters to build the highlight. That is ONE law with
// two halves standing on opposite sides of the wire: whatever the server writes, the client must
// unwrite, exactly.
//
// It was TWO COPIES until R13. `HIT_OPEN`/`HIT_CLOSE` were declared in `server/db/library.js` and
// again in `src/App.tsx`, and the check holding them together compared each to a THIRD copy of the
// character typed into the fixture. Change the server's pair and nothing goes red — the member just
// gets raw ␟ and ␞ control glyphs in her search results, which is the quiet kind of wrong. A law with
// two copies is a law with two answer keys; that is the same disease that burned four paid renders
// per Hebrew seal, and it is fixed here the same way: one definition, imported by both.
//
// Imports NOTHING, so `scripts/r13-search-hit.mjs` loads and RUNS it, and so does the server.

// Sentinels, NOT html. Chosen because they are the two ASCII separator control characters: no book
// page contains them, a member cannot type them into the search box, and ts_headline takes them as
// StartSel/StopSel without escaping. The DOM only ever receives plain strings.
export const HIT_OPEN = "␟"; // ␟ unit separator
export const HIT_CLOSE = "␞"; // ␞ record separator

// Split a marked snippet into ordered runs of text, each flagged hit or not.
//
// THE THREE PROPERTIES THIS OWES THE MEMBER, and R13-3 measures all three on the returned array:
//   1. LOSSLESS — concatenating every run's text reproduces the snippet with only the markers gone.
//      A parser that eats a character on a malformed input silently truncates a book passage.
//   2. NO SENTINEL SURVIVES — a stray, reversed or unbalanced marker degrades to PLAIN TEXT and the
//      marker character itself is dropped, never rendered. The old parser pushed the remainder
//      verbatim when it ran out of pairs, so a lone opener put a control glyph on screen.
//   3. ORDERED — the runs come back in the order they appear, so the caller renders the passage as
//      it reads rather than as the matches happen to sort.
export function splitSnippet(snippet) {
  const text = String(snippet == null ? "" : snippet);
  const parts = [];
  const push = (t, hit) => {
    if (!t) return;
    // A marker that never found its pair is not content. Drop the character, keep the words — and
    // do it on BOTH kinds of run, not just the plain ones. `␟␟doubled␞` puts the second opener
    // INSIDE the matched run, where a plain-runs-only scrub never sees it; the gate caught exactly
    // that. No sentinel reaches the DOM means no sentinel reaches the DOM.
    const clean = t.split(HIT_OPEN).join("").split(HIT_CLOSE).join("");
    if (clean) parts.push({ text: clean, hit });
  };
  let rest = text;
  for (;;) {
    const a = rest.indexOf(HIT_OPEN);
    if (a < 0) break;
    const b = rest.indexOf(HIT_CLOSE, a + HIT_OPEN.length);
    if (b < 0) break; // an opener with no closer: whatever is left is plain text.
    push(rest.slice(0, a), false);
    push(rest.slice(a + HIT_OPEN.length, b), true);
    rest = rest.slice(b + HIT_CLOSE.length);
  }
  push(rest, false);
  return parts;
}

// The plain reading of a snippet, markers removed. Used wherever a hit needs to be text rather than
// a tree (a title attribute, a log line, a gate reading back what a member would see).
export function plainSnippet(snippet) {
  return splitSnippet(snippet).map((p) => p.text).join("");
}
